import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container
} from '@mui/material';
import { listBackups, createBackup, runBackup } from '../../api/axios';

const BackupManager = () => {
  const { t } = useTranslation('global');

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  

  // Funkcija za dobavljanje liste backup-a
  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await listBackups();
      console.log(response);
      
      if (response.success) {
        setBackups(response.backups || []);
      } else {
        setError(response.message || t("backupManager.failedToFetchBackups"));
      }
    } catch (err) {
      setError(err.message || t("backupManager.errorFetchingBackups"));
    } finally {
      setLoading(false);
    }
  };

  // Funkcija za kreiranje novog backup-a
  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      const response = await createBackup();
      
      if (response.success) {
        setMessage(t("backupManager.backupCreatedSuccessfully"));
        // Osveži listu nakon kreiranja novog backup-a
        await fetchBackups();
      } else {
        setError(response.message || t("backupManager.failedToCreateBackup"));
      }
    } catch (err) {
      setError(err.message || t("backupManager.errorCreatingBackup"));
    } finally {
      setLoading(false);
    }
  };

  // Učitaj listu backup-a prilikom mount-a komponente
  useEffect(() => {
    fetchBackups();
  }, []);

  // Formatiranje veličine fajla u čitljiv oblik
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  //Restore funkcija - pokretanje sql fajlova
  const handleRestore = async (filename) => {
  if (window.confirm( t("backupManager.areYouSureYouWantToRestore") + ` ${filename}?`)) {
    try {
      setLoading(true);
      const response = await runBackup(filename);
      if (response.success) {
        setMessage(`${filename} ` + t("backupManager.restoredSuccessfully"));
      } else {
        setError(response.message || t("backupManager.failedToRestoreBackup"));
      }
    } catch (err) {
      setError(err.message || t("backupManager.errorRestoringBackup"));
    } finally {
      setLoading(false);
    }
  }
};

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          {t("backupManager.databaseBackupManager")}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleCreateBackup} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('backupManager.processing') : t('backupManager.createBackup')}
          </Button>
        </Box>
        
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
      
      <Box>
        <Typography variant="h6" component="h3" gutterBottom>
          {t("backupManager.availableBackups")}
        </Typography>
        
        {loading && backups.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : backups.length === 0 ? (
          <Typography variant="body1" sx={{ py: 2 }}>
            {t("backupMangager.noBackupsAvailable")}
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("backupManager.fileName")}</TableCell>
                  <TableCell>{t("backupManager.size")}</TableCell>
                  <TableCell>{t("backupManager.modified")}</TableCell>
                  <TableCell>{t("backupManager.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup, index) => (
                  <TableRow key={index}>
                    <TableCell>{backup.filename}</TableCell>
                    <TableCell>{formatFileSize(backup.size)}</TableCell>
                    <TableCell>{new Date(backup.modified).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined"
                        color="primary"
                        disabled={loading}
                        onClick={() => handleRestore(backup.filename)}
                      >
                        {loading ? <CircularProgress size={24} /> : t("backupManager.restore")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
};

export default BackupManager;